import { useFlickity } from '@/components/hooks/useFlickity'
import { BaseResults } from 'libs/athos/components/recommendations/BaseResults'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { useState, useEffect } from 'preact/hooks'
import clsx from 'clsx'
import { shouldHideCarouselArrows } from '@/components/helpers/carousel'

export function CarouselResults({
  className,
  wrapperClassName = '',
  options = { pageDots: true, cellAlign: 'left' },
  productCardSettings,
  beaconEnabled = true,
  'data-dl-section-id': dataDlSectionId
}) {
  const { results, isLoading } = useAthosContext()
  const [realNode, setRealNode] = useState()
  const [containerNode, setContainerNode] = useState()
  const [showRealCarousel, setShowRealCarousel] = useState(false)
  const [hideArrows, setHideArrows] = useState(false)

  // The skeleton is a static placeholder, not a carousel. It previously called
  // `useFlickity(skeletonNode, options, [])`, but with empty deps that effect ran once
  // while the ref was still undefined and never re-ran, so Flickity was never actually
  // created — confirmed on device, where the skeleton wrapper reports 0 B of layer memory.
  // Now that `useFlickity` correctly depends on `target`, leaving that call in place would
  // have started a real Flickity instance (and its promoted slider layer) on every
  // recommendations carousel for the first time. Removing it keeps today's behaviour.
  const { SliderPortal, flickity } = useFlickity(realNode, options, [results])

  const hasData = !isLoading && results?.results

  useEffect(() => {
    const cellCount = results?.results?.length ?? 0
    const updateArrowsVisibility = () => setHideArrows(shouldHideCarouselArrows(cellCount, options.columns, options.columnsMb))
    updateArrowsVisibility()
    window.addEventListener('resize', updateArrowsVisibility)
    return () => window.removeEventListener('resize', updateArrowsVisibility)
  }, [results, options.columns, options.columnsMb])

  useEffect(() => {
    if (hasData && flickity && containerNode) {
      const skeletonHeight = containerNode.getBoundingClientRect().height
      containerNode.style.height = `${skeletonHeight}px`

      flickity.resize()
      flickity.reloadCells()

      const timer = setTimeout(() => {
        containerNode.style.height = ''
        setShowRealCarousel(true)
      }, 100)

      return () => clearTimeout(timer)
    } else {
      setShowRealCarousel(false)
    }
  }, [hasData, flickity, containerNode])

  return (
    <BaseResults
      className={className}
      wrapperClassName={wrapperClassName}
      productCardSettings={productCardSettings}
      beaconEnabled={beaconEnabled}
      insideCarousel={true}
      renderWrapper={(skeletonItems, realItems) => (
        <div ref={setContainerNode} className="relative w-full grid grid-cols-1 grid-rows-1 transition-all">
          <div className={clsx(
            'w-full row-start-1 col-start-1 transition-opacity',
            !showRealCarousel ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}>
            <div
              className={clsx(
                'flex w-full',
                options.pageDots && 'pb-1.5 lg:pb-1.625'
              )}
            >
              {skeletonItems}
            </div>
          </div>

          <div className={clsx(
            'w-full row-start-1 col-start-1 transition-opacity',
            showRealCarousel ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}>
            <div
              ref={setRealNode}
              className={clsx(
                'flex w-full',
                options.pageDots && 'pb-1.5 lg:pb-1.625',
                hideArrows && 'carousel-arrows-hidden'
              )}
              data-dl-section-id={dataDlSectionId}
            >
              <SliderPortal>
                {realItems}
              </SliderPortal>
            </div>
          </div>
        </div>
      )}
    />
  )
}
